import styled from "styled-components";
import { PrimaryColor, SecondaryColor, TertiaryColor, BackgroundColor, BorderColor, OffColor } from "global.styles";

export const Overlay = styled.div`
    position: fixed;
    top: 0; left: 0; right: 0; bottom: 0;
    z-index: 500;
    background: rgba(0, 0, 0, 0.55);
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 16px;
    box-sizing: border-box;
`;

export const Modal = styled.div`
    background: #fff;
    border-radius: 4px;
    width: 100%;
    max-width: 1300px;
    max-height: 90vh;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.25);
`;

export const ModalHeader = styled.div`
    display: flex;
    align-items: center;
    padding: 12px 20px;
    border-bottom: 1px solid #e0e0e0;
    background: ${BackgroundColor};
    flex-shrink: 0;

    h2 {
        margin: 0 0 0 12px;
        font-size: 18px;
    }
`;

export const BackButton = styled.button`
    background: none;
    border: none;
    cursor: pointer;
    font-size: 14px;
    color: #333;
    padding: 4px 8px 4px 0;

    &:hover {
        text-decoration: underline;
    }
`;

export const CheckoutBody = styled.div`
    display: grid;
    grid-template-columns: 1fr 600px;
    flex: 1;
    min-height: 0;
    overflow: hidden;

    @media (max-width: 960px) {
        grid-template-columns: 1fr;
        grid-template-rows: auto 1fr;
        overflow-y: auto;
    }
`;

export const StepsCol = styled.div`
    padding: 20px 24px;
    overflow-y: auto;

    @media (max-width: 960px) {
        order: 2;
        padding: 14px 16px;
        overflow-y: visible;
    }

    /* Collapse 2-column field rows to single column on phones */
    @media (max-width: 560px) {
        .columns--2 {
            flex-direction: column;

            > *:first-child {
                margin-right: 0;
                margin-bottom: 0;
            }
            > *:last-child {
                margin-left: 0;
            }
        }
    }
`;

export const CartCol = styled.div`
    padding: 16px;
    border-left: 1px solid #e0e0e0;
    overflow-y: auto;
    background: ${BackgroundColor};

    @media (max-width: 960px) {
        order: 1;
        border-left: none;
        border-bottom: 1px solid #e0e0e0;
        overflow-y: visible;
    }
`;

export const CartImage = styled.img`
    width: 100%;
    margin-bottom: 12px;
    display: block;

    @media (max-width: 960px) {
        display: none;
    }
`;

export const SectionLabel = styled.div`
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    color: ${OffColor};
    margin-bottom: 8px;

    & + & {
        margin-top: 10px;
        color: #aaa;
    }
`;

export const CartItemRow = styled.div`
    display: flex;
    align-items: center;
    padding: 6px 0;
    border-bottom: 1px solid #f0f0f0;

    img {
        margin-right: 8px;
        min-width: 44px;
        object-fit: contain;
    }
`;

export const CartItemDetails = styled.div`
    flex: 1;
    min-width: 0;
    text-align: left;

    .sku {
        font-size: 10px;
        color: #999;
    }

    .title {
        font-size: 12px;
        font-weight: 600;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }
`;

export const CartItemQty = styled.div`
    margin-right: 8px;
    color: #666;
    font-size: 12px;
`;

export const CartItemPrice = styled.div`
    text-align: right;
    font-size: 12px;

    .original {
        text-decoration: line-through;
        color: #bbb;
        font-size: 11px;
    }

    .amount {
        font-weight: 600;
    }
`;

export const CartTotals = styled.div`
    margin-top: 12px;
    padding-top: 10px;
    border-top: 1px solid #e0e0e0;
`;

export const PriceLine = styled.div`
    display: flex;
    justify-content: space-between;
    font-size: 12px;
    margin-bottom: 4px;

    span:first-child {
        color: #666;
    }

    &.delivery {
        margin-bottom: 8px;
    }
`;

export const TotalLine = styled.div`
    display: flex;
    justify-content: space-between;
    font-weight: 700;
    font-size: 14px;
    padding-top: 8px;
    border-top: 2px solid #333;
`;

/* ── Accordion ─────────────────────────────────────────── */

export const StepCard = styled.div<{ locked?: boolean }>`
    border: 1px solid #e0e0e0;
    border-radius: 4px;
    margin-bottom: 8px;
    overflow: hidden;
    opacity: ${p => p.locked ? 0.45 : 1};
`;

export const StepHeader = styled.div<{ active?: boolean; done?: boolean }>`
    display: flex;
    align-items: center;
    gap: 13px;
    padding: 16px 24px;
    cursor: ${p => (p.done && !p.active) ? "pointer" : "default"};
    background: ${SecondaryColor};
    border-bottom: ${p => p.active ? "none" : `1px solid ${TertiaryColor}`};
    border-left: ${p => p.active ? `3px solid #fff` : "3px solid transparent"};
    user-select: none;
    box-sizing: border-box;
`;

export const StepNumber = styled.div<{ active?: boolean; done?: boolean }>`
    font-size: 13px;
    font-weight: 700;
    flex-shrink: 0;
    min-width: 18px;
    color: ${p => (p.active || p.done) ? "rgba(247,247,249,0.9)" : "rgba(247,247,249,0.35)"};
`;

export const StepTitle = styled.div<{ active?: boolean; done?: boolean }>`
    font-weight: 600;
    font-size: 14px;
    text-align: left;
    color: ${p => p.active ? "#fff" : p.done ? "rgba(247,247,249,0.8)" : "rgba(247,247,249,0.4)"};
`;

export const StepSummary = styled.div`
    font-size: 12px;
    color: rgba(247, 247, 249, 0.5);
    margin-top: 1px;
    text-align: left;
`;

export const EditLink = styled.span`
    font-size: 12px;
    color: rgba(247, 247, 249, 0.6);
    text-decoration: underline;
    margin-left: auto;
    flex-shrink: 0;
`;

export const StepBody = styled.div`
    padding: 16px 20px;
    border-top: 1px solid #ececec;
    background: #fff;
`;

export const AddressDivider = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    margin: 16px 0 8px;
    font-size: 12px;
    font-weight: 700;
    text-transform: uppercase;
    color: ${OffColor};

    &::before, &::after {
        content: "";
        flex: 1;
        border-top: 1px solid #e0e0e0;
    }
`;

export const ShipToggleLabel = styled.label`
    display: flex;
    align-items: center;
    gap: 8px;
    margin-top: 14px;
    font-size: 13px;
    color: #444;
    cursor: pointer;
    user-select: none;

    input[type="checkbox"] {
        accent-color: ${SecondaryColor};
        width: 15px;
        height: 15px;
        flex-shrink: 0;
    }
`;

export const StepActions = styled.div`
    display: flex;
    justify-content: flex-end;
    margin-top: 18px;
`;

export const PrimaryButton = styled.button`
    background: ${PrimaryColor};
    color: #fff;
    border: none;
    padding: 9px 26px;
    border-radius: 2px;
    cursor: pointer;
    font-weight: 700;
    font-size: 14px;

    &:hover {
        filter: brightness(1.08);
    }

    &:active {
        filter: brightness(0.95);
    }
`;

/* ── Delivery options ───────────────────────────────────── */

export const DeliveryOptions = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
`;

export const DeliveryOption = styled.label<{ selected?: boolean }>`
    display: flex;
    align-items: center;
    justify-content: space-between;
    border: 2px solid ${p => p.selected ? "#333" : "#e0e0e0"};
    border-radius: 4px;
    padding: 13px 16px;
    cursor: pointer;
    background: ${p => p.selected ? "#f9f9f9" : "#fff"};
    transition: border-color 0.15s, background 0.15s;

    &:hover {
        border-color: ${p => p.selected ? "#333" : BorderColor};
    }

    input[type="radio"] {
        accent-color: #333;
        width: 17px;
        height: 17px;
    }
`;

export const DeliveryLabel = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;

    span {
        font-weight: 600;
        font-size: 14px;
    }
`;

export const DeliveryPrice = styled.span`
    font-size: 14px;
    font-weight: 600;
`;

/* ── Summary box (payment step) ─────────────────────────── */

export const SummaryBox = styled.div`
    background: #f7f7f7;
    border-radius: 4px;
    padding: 12px 14px;
    margin-bottom: 14px;
    font-size: 14px;
`;

export const SummaryLine = styled.div`
    display: flex;
    justify-content: space-between;
    margin-bottom: 6px;

    span:first-child {
        color: #666;
    }
`;

export const SummaryDivider = styled.div`
    display: flex;
    justify-content: space-between;
    padding-bottom: 8px;
    border-bottom: 1px solid #e0e0e0;
    margin-bottom: 8px;

    span:first-child {
        color: #666;
    }
`;

export const SummaryTotal = styled.div`
    display: flex;
    justify-content: space-between;
    font-weight: 700;
    font-size: 15px;
`;

/* ── Success panel ──────────────────────────────────────── */

export const SuccessPanel = styled.div`
    padding: 48px;
    text-align: center;

    h3 {
        margin-bottom: 12px;
    }

    p {
        color: #555;
        margin-bottom: 28px;
    }
`;

/* ── Error banner ──────────────────────────────────────── */
export const ErrorBanner = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    background: #fff3cd;
    border-top: 1px solid #ffc107;
    border-bottom: 1px solid #ffc107;
    padding: 12px 24px;
    color: #856404;
    font-size: 14px;
    line-height: 1.4;
    flex-shrink: 0;

    button {
        background: none;
        border: none;
        cursor: pointer;
        font-size: 18px;
        color: #856404;
        padding: 0 0 0 12px;
        line-height: 1;
        flex-shrink: 0;
    }
`;

/* ── Order confirmation (overlay) ─────────────────────── */

export const ConfirmationPage = styled.div`
    position: fixed;
    top: 0; left: 0; right: 0; bottom: 0;
    background: rgba(0, 0, 0, 0.6);
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 24px;
    box-sizing: border-box;
    z-index: 1000;
`;

export const ConfirmationCard = styled.div`
    background: #fff;
    border-radius: 4px;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.25);
    width: 100%;
    max-width: 480px;
    overflow: hidden;
`;

export const ConfirmationImage = styled.img`
    width: 100%;
    display: block;
    background: ${BackgroundColor};
`;

export const ConfirmationHeader = styled.div`
    background: ${SecondaryColor};
    padding: 24px 32px;
    text-align: center;

    h2 {
        color: #fff;
        margin: 0 0 4px;
        font-size: 20px;
    }

    p {
        color: rgba(247, 247, 249, 0.65);
        margin: 0;
        font-size: 14px;
    }
`;

export const ConfirmationBody = styled.div`
    padding: 20px 28px;
`;

export const ConfirmationDetail = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    padding: 7px 0;
    border-bottom: 1px solid #f0f0f0;
    font-size: 14px;

    &:last-of-type {
        border-bottom: none;
    }

    span:first-child {
        color: ${OffColor};
        flex-shrink: 0;
        margin-right: 16px;
    }

    span:last-child {
        text-align: right;
        font-weight: 600;
    }
`;

export const ConfirmationActions = styled.div`
    padding: 0 28px 24px;
    display: flex;
    justify-content: center;
`;
